import contactOwnerFormActions from '../actions/contactOwnerFormActions';
import edapActions from '../actions/edapActions';
import guestSelectorActions from '../actions/guestSelectorActions';
import listingActions from '../actions/listingActions';
import tripActions from '../actions/tripActions';
import {OpenModalStates} from '../records/listingPageRecord';
import {hasTooManyGuests} from '../utils/guests';
import {logExperiment} from '../../universal/utils/abacusHelper';
import {HA_PTA_SEARCHBOX_ADD_AGE_OF_CHILDREN} from '../abacus/experiments';

function isNotUndefined(field) {
    return typeof field !== 'undefined';
}

function getChangedNumber(value, previous) {
    if (isNotUndefined(value)) {
        return value < 0 ? 0 : value;
    }

    return previous;
}

function sanitizeLargeNumber(num, max) {
    return num >= max ? max : num;
}

function triggerGuestOpenedEdapEvent(currentModalState, store) {
    if (currentModalState === 'INQUIRY') {
        store.dispatch(
            edapActions.triggerEdapEvent('generic.event', {
                eventcategory: 'guest selector',
                eventaction: 'inquiry guest opened'
            })
        );
    } else if (currentModalState === 'REQUEST_TO_BOOK') {
        store.dispatch(
            edapActions.triggerEdapEvent('generic.event', {
                eventcategory: 'guest selector',
                eventaction: 'booking guest opened'
            })
        );
    }
}

function guestSelectorChanged(store, action = {payload: {}}) {
    const {fieldsChanged} = action.payload;
    const {guestSelectorDataUpdated} = guestSelectorActions;

    // Only a subset of fields will be updated here.
    // We only want to make a call out to the rates service if the information
    // is valid as much as we think it can be.
    // Start off by seeing if the dates are valid in the store.
    const state = store.getState();
    const guestSelectorData = state.guestSelectorReducer;
    const listingData = state.listingReducer;
    let adults = guestSelectorData.adults;
    let children = guestSelectorData.children;
    let pets = guestSelectorData.pets;

    adults = getChangedNumber(fieldsChanged.adults, adults);
    children = getChangedNumber(fieldsChanged.children, children);

    if (isNotUndefined(fieldsChanged.pets)) {
        pets = fieldsChanged.pets;
    }

    if (hasTooManyGuests({adults, children, sleeps: listingData.sleeps})) {
        // Too many were selector.  Clamp to the current 'valid' amount.
        adults = sanitizeLargeNumber(adults, guestSelectorData.adults);
        children = sanitizeLargeNumber(children, guestSelectorData.children);
    }

    store.dispatch(
        guestSelectorDataUpdated({
            adults,
            children,
            pets
        })
    );
}

function getNextOpenModalState(state) {
    const currentModalState = state.listingPageReducer.openModalState;
    const isBookingFlow = state.tripDetailsReducer.isBookingFlow;

    if (isBookingFlow) {
        return OpenModalStates.BOOKING_FLOW_GUEST_SELECTOR;
    }

    const nextStates = {
        [OpenModalStates.NONE]: OpenModalStates.MAIN_PAGE_GUEST_SELECTOR,
        [OpenModalStates.REQUEST_TO_BOOK]: OpenModalStates.REQUEST_TO_BOOK_GUEST_SELECTOR,
        [OpenModalStates.INQUIRY]: OpenModalStates.INQUIRY_GUEST_SELECTOR,
        [OpenModalStates.MAIN_PAGE_DATE_PICKER]: OpenModalStates.MAIN_PAGE_GUEST_SELECTOR,
        [OpenModalStates.REQUEST_TO_BOOK_DATE_PICKER]:
            OpenModalStates.REQUEST_TO_BOOK_GUEST_SELECTOR,
        [OpenModalStates.INQUIRY_DATE_PICKER]: OpenModalStates.INQUIRY_GUEST_SELECTOR
    };

    return nextStates[currentModalState] || currentModalState;
}

function getNextCloseModalState(state, isCancel) {
    const currentModalState = state.listingPageReducer.openModalState;
    const isBookingFlow = state.tripDetailsReducer.isBookingFlow;

    if (isBookingFlow) {
        return isCancel ? OpenModalStates.NONE : OpenModalStates.REQUEST_TO_BOOK;
    }

    const nextStates = {
        [OpenModalStates.MAIN_PAGE_GUEST_SELECTOR]: OpenModalStates.NONE,
        [OpenModalStates.REQUEST_TO_BOOK_GUEST_SELECTOR]: OpenModalStates.REQUEST_TO_BOOK,
        [OpenModalStates.INQUIRY_GUEST_SELECTOR]: OpenModalStates.INQUIRY
    };

    return nextStates[currentModalState] || currentModalState;
}

function openGuestSelectorModal(store) {
    const {guestSelectorDataUpdated} = guestSelectorActions;

    const state = store.getState();
    const tripReducer = state.tripDetailsReducer;
    const currentModalState = state.listingPageReducer.openModalState;
    const nextModalState = getNextOpenModalState(state);

    // Update the guest selector with the data we have in our Trip
    store.dispatch(
        guestSelectorDataUpdated({
            adults: tripReducer.adults,
            children: tripReducer.children,
            pets: tripReducer.hasPets
        })
    );

    logExperiment(HA_PTA_SEARCHBOX_ADD_AGE_OF_CHILDREN);

    triggerGuestOpenedEdapEvent(currentModalState, store);

    store.dispatch(listingActions.updateModalState(nextModalState));
}

function closeGuestSelectorModal(store, action = {payload: {}}) {
    const {isCancel} = action.payload;

    const state = store.getState();
    const nextModalState = getNextCloseModalState(state, isCancel);

    store.dispatch(listingActions.updateModalState(nextModalState));

    if (isCancel) {
        store.dispatch(tripActions.setBookingFlow(false));
    }
}

function submitGuestSelectorModal(store, action = {payload: {}}) {
    const state = store.getState();
    const guestSelectorData = state.guestSelectorReducer;
    const tripData = state.tripDetailsReducer;

    action.payload.fieldsChanged = action.payload.fieldsChanged || {};

    if (tripData.children !== guestSelectorData.children) {
        action.payload.fieldsChanged.children = guestSelectorData.children;
    }

    if (tripData.adults !== guestSelectorData.adults) {
        action.payload.fieldsChanged.adults = guestSelectorData.adults;
    }

    if (tripData.hasPets !== guestSelectorData.pets) {
        action.payload.fieldsChanged.hasPets = guestSelectorData.pets;
    }

    store.dispatch(contactOwnerFormActions.contactOwnerFormFieldValid('guests', true));
    store.dispatch(tripActions.tripDetailsChanged(action.payload.fieldsChanged));
}

export default guestSelectorChanged;
export {
    closeGuestSelectorModal,
    getChangedNumber,
    guestSelectorChanged,
    openGuestSelectorModal,
    triggerGuestOpenedEdapEvent,
    sanitizeLargeNumber,
    submitGuestSelectorModal,
    getNextOpenModalState,
    getNextCloseModalState
};
